import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL, API_KEY, boardUrl } from "../config/config";

const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
	const [userIdDecrypting, setUserIdDecrypting] = useState(false);
	const [userId, setUserId] = useState(null);

	const [visitorLoading, setVisitorLoading] = useState(false);
	const [visitor, setVisitor] = useState(null);

	const decryptUserId = useCallback(async () => {
		if (userId !== null) return;

		if (visitor !== null) return;

		if (userIdDecrypting) return; // Decrypting

		// Decrypt user ID from localStorage
		const encryptedUserId = localStorage.getItem('encryptedUserId');
		if (encryptedUserId) {
			try {
				setUserIdDecrypting(true);

				let config = {
					method: 'get',
					maxBodyLength: Infinity,
					url: `${boardUrl}/user-storage/decrypt-user-id?encrypted_user_id=${encryptedUserId}`,
					withCredentials: true
				};

				axios
					.request(config)
					.then((response) => {
						if (response.data.user_id) {
							setUserId(Number.parseInt(response.data.user_id));
						}

						setUserIdDecrypting(false);
					});
			} catch (error) {
				setUserIdDecrypting(false);
				setUserId(null);
				setVisitor({
					user_id: 0,
					username: 'Guest'
				}); // Set as guest
			}
		} else {
			setUserId(null);
			setVisitor({
				user_id: 0,
				username: 'Guest'
			}); // Set as guest
		}
	}, [userId, userIdDecrypting, visitor]);

	const fetchUser = useCallback(async () => {
		if (!userId) return;

		// Check sessionStorage for cached user data
		let cachedUser = sessionStorage.getItem(`user_${userId}`);
		if (cachedUser) {
			cachedUser = JSON.parse(cachedUser);

			if (cachedUser.user_id && cachedUser.user_id === userId) {
				setVisitor(cachedUser);
				return;
			}
		}

		if (visitorLoading) return; // Loading

		if (visitor) return; // Already loaded

		try {
			setVisitorLoading(true);

			let config = {
				method: 'get',
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/me`,
				headers: {
					'XF-Api-Key': API_KEY,
					'XF-Api-User': userId
				},
				withCredentials: true
			};

			axios
				.request(config)
				.then((response) => {
					setVisitor(response.data.me);
					// Cache user data in sessionStorage
					sessionStorage.setItem(`user_${userId}`, JSON.stringify(response.data.me));

					setVisitorLoading(false);
				});
		} catch (error) {
			console.error('Failed to fetch user:', error);
			setVisitorLoading(false);
		}
	}, [userId, visitor, visitorLoading]);

	useEffect(() => {
		decryptUserId().then(() => {
			if (userId && !visitor) {
				fetchUser();
			}
		});
	}, [decryptUserId, fetchUser, userId, visitor]);

	return (
		<AuthContext.Provider value={{ visitor }}>
			{children}
		</AuthContext.Provider>
	);
};

export { AuthContext, AuthProvider };
